/* Minimal, dependency-free Markdown renderer for job detail sections.
 * Supports: # H1, ## H2, ### H3, **bold**, *italic*, `code`,
 * unordered lists (- or *), ordered lists (1.), pipe tables,
 * paragraphs, line breaks, [text](url) links.
 * Not a full Markdown spec — just enough for our LLM-generated sections.
 */
function escapeHtml(s) {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function renderInline(text) {
  let t = escapeHtml(text);
  t = t.replace(/`([^`]+)`/g, '<code class="bg-slate-100 text-orange-700 px-1 py-0.5 rounded text-[0.9em]">$1</code>');
  t = t.replace(/\*\*([^*]+)\*\*/g, '<strong class="font-bold text-slate-900">$1</strong>');
  t = t.replace(/\*([^*]+)\*/g, '<em>$1</em>');
  t = t.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-orange-600 hover:underline font-medium">$1</a>');
  return t;
}

function renderTable(lines) {
  if (lines.length < 2) return "";
  const head = lines[0].trim().replace(/^\||\|$/g, "").split("|").map((c) => c.trim());
  const rows = lines.slice(2).map((l) => l.trim().replace(/^\||\|$/g, "").split("|").map((c) => c.trim()));
  let html = '<div class="overflow-x-auto my-4 rounded-lg border border-slate-200"><table class="w-full text-sm">';
  html += '<thead class="bg-slate-100"><tr>' + head.map((h) => `<th class="px-3 py-2 text-left font-bold text-slate-900 border-b border-slate-200">${renderInline(h)}</th>`).join("") + "</tr></thead>";
  html += "<tbody>" + rows.map((r) => "<tr class=\"border-b border-slate-100 hover:bg-orange-50/30\">" + r.map((c) => `<td class="px-3 py-2 text-slate-700">${renderInline(c)}</td>`).join("") + "</tr>").join("") + "</tbody>";
  html += "</table></div>";
  return html;
}

export default function renderMarkdown(md) {
  if (!md) return "";
  const lines = md.split(/\r?\n/);
  const out = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];

    // Tables — detect | sep on this and next line
    if (/^\s*\|.+\|/.test(line) && i + 1 < lines.length && /^\s*\|[-:\s|]+\|\s*$/.test(lines[i + 1])) {
      const tableLines = [line, lines[i + 1]];
      i += 2;
      while (i < lines.length && /^\s*\|.+\|/.test(lines[i])) {
        tableLines.push(lines[i]);
        i++;
      }
      out.push(renderTable(tableLines));
      continue;
    }

    // Headings
    const h3 = line.match(/^###\s+(.+)/);
    const h2 = line.match(/^##\s+(.+)/);
    const h1 = line.match(/^#\s+(.+)/);
    if (h3) { out.push(`<h3 class="font-bold text-lg text-slate-900 mt-5 mb-2" style="font-family:'Cabinet Grotesk',sans-serif">${renderInline(h3[1])}</h3>`); i++; continue; }
    if (h2) { out.push(`<h2 class="font-extrabold text-xl text-slate-900 mt-6 mb-3" style="font-family:'Cabinet Grotesk',sans-serif">${renderInline(h2[1])}</h2>`); i++; continue; }
    if (h1) { out.push(`<h1 class="font-extrabold text-2xl text-slate-900 mt-6 mb-3" style="font-family:'Cabinet Grotesk',sans-serif">${renderInline(h1[1])}</h1>`); i++; continue; }

    // Unordered list
    if (/^\s*[-*]\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*[-*]\s+/.test(lines[i])) {
        items.push(`<li class="text-slate-700 leading-relaxed">${renderInline(lines[i].replace(/^\s*[-*]\s+/, ""))}</li>`);
        i++;
      }
      out.push(`<ul class="list-disc pl-6 my-3 space-y-1">${items.join("")}</ul>`);
      continue;
    }

    // Ordered list
    if (/^\s*\d+\.\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) {
        items.push(`<li class="text-slate-700 leading-relaxed">${renderInline(lines[i].replace(/^\s*\d+\.\s+/, ""))}</li>`);
        i++;
      }
      out.push(`<ol class="list-decimal pl-6 my-3 space-y-1">${items.join("")}</ol>`);
      continue;
    }

    // Blank line
    if (line.trim() === "") { i++; continue; }

    // Paragraph
    const para = [line];
    i++;
    while (i < lines.length && lines[i].trim() !== "" && !/^[#\-*]/.test(lines[i]) && !/^\s*\d+\.\s+/.test(lines[i]) && !/^\s*\|/.test(lines[i])) {
      para.push(lines[i]);
      i++;
    }
    out.push(`<p class="text-slate-700 leading-relaxed my-3">${renderInline(para.join(" "))}</p>`);
  }
  return out.join("");
}
